import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'sonner';
import { guardrailsService } from '../services/GuardrailsService';

interface Guardrail {
  id: string;
  guardrail_name: string;
  updated_at: string;
}

export function GuardrailsListPage() {
  const navigate = useNavigate();
  // const [guardrails, setGuardrails] = useState<Guardrail[]>(mockGuardrails);
  const [guardrails, setGuardrails] = useState<Guardrail[]>([]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGuardrail, setSelectedGuardrail] = useState<Guardrail | null>(null);
  useEffect(() => {
      fetchGuardrails();
    }, []);

  const fetchGuardrails = async () => {
    try {
        const res = await guardrailsService.getAllGuardrails(); 
        setGuardrails(res);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load guardrails");
      }
    };


  const handleCreate = () => {
    navigate('/guardrails/new');
  };

  const handleEdit = (guardrail: Guardrail) => {
    navigate(`/guardrails/${guardrail.id}`);
  };

  const handleDeleteClick = (guardrail: Guardrail) => {
    setSelectedGuardrail(guardrail);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedGuardrail) return;

    try {
      await guardrailsService.deleteGuardrailById(selectedGuardrail.id); // <-- YOUR DELETE API

      setGuardrails(prev => prev.filter(g => g.id !== selectedGuardrail.id));
      toast.success(`Guardrail "${selectedGuardrail.guardrail_name}" deleted successfully`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete guardrail");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedGuardrail(null);
    }
  };


  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedGuardrail(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fafbfc' }}>
      {/* Header */}
      <Box 
        sx={{ 
          p: 4, 
          pb: 3,
          bgcolor: '#fff',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: '#0f172a',
                mb: 0.5
              }}
            >
              Guardrails
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and configure guardrails for your AI models
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.25,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
              }
            }}
          >
            Create Guardrail
          </Button>
        </Box>
      </Box>

      {/* Table Section */}
      <Box sx={{ flex: 1, p: 4, overflow: 'auto' }}>
        <TableContainer 
          component={Paper} 
          elevation={0}
          sx={{ 
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafbfc' }}>
                <TableCell 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#475569',
                    fontSize: '0.813rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    py: 2
                  }}
                >
                  Guardrail Name
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#475569',
                    fontSize: '0.813rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    py: 2
                  }}
                >
                  Last Updated
                </TableCell>
                {/* <TableCell 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#475569',
                    fontSize: '0.813rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    py: 2
                  }}
                >
                  Status
                </TableCell> */}
                <TableCell 
                  align="right"
                  sx={{ 
                    fontWeight: 700, 
                    color: '#475569',
                    fontSize: '0.813rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    py: 2
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {guardrails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">
                      No guardrails found. Create your first guardrail to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                guardrails.map((guardrail) => (
                  <TableRow 
                    key={guardrail.id}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: '#f8fafc' 
                      },
                      '&:last-child td': {
                        borderBottom: 0
                      }
                    }}
                  >
                    <TableCell sx={{ py: 2.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: '#1e293b'
                        }}
                      >
                        {guardrail.guardrail_name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(guardrail.updated_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(guardrail)}
                        sx={{
                          color: '#64748b',
                          mr: 1,
                          '&:hover': {
                            bgcolor: '#f1f5f9',
                            color: '#2563eb'
                          }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(guardrail)}
                        sx={{
                          color: '#64748b',
                          '&:hover': {
                            bgcolor: '#fef2f2',
                            color: '#dc2626'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Delete Guardrail
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedGuardrail?.guardrail_name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDeleteCancel}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748b'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
